import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Notification from "../../components/Notification";
import { API_URL } from "../../config";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faSave } from '@fortawesome/free-solid-svg-icons';

function ProfileUpdate() {
    const [profile, setProfile] = useState({
        first_name: "",
        last_name: "",
        phone_number: "",
        email: "",
        status: ""
    });
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem("access_token");

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_URL}/profile`, {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setProfile(data);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch profile data");
                }
            } catch (error) {
                setNotification({ type: "danger", message: error.message });
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [navigate, token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfile(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        try {
            const response = await fetch(`${API_URL}/profile`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(profile)
            });

            if (response.ok) {
                setNotification({ 
                    type: "success", 
                    message: "Profile updated successfully" 
                });
                // Redirect after 2 seconds
                setTimeout(() => navigate("/dashboard/profile"), 2000);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                const errorData = await response.json();
                setErrors(errorData.errors || {});
                setNotification({ 
                    type: "danger", 
                    message: errorData.message || "Failed to update profile" 
                });
            }
        } catch (error) {
            setNotification({ 
                type: "danger", 
                message: "An error occurred while updating profile" 
            });
            console.error("Update error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) {
        return (
            <div className="container">
                <div className="section">
                    <p className="has-text-centered py-6">Loading profile data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="section">
                {/* Header Section */}
                <div className="level">
                    <div className="level-left">
                        <h1 className="title is-3">Update Profile</h1>
                    </div>
                    <div className="level-right">
                        <div className="buttons">
                            <Link to="/dashboard/profile" className="button is-light">
                                <span className="icon">
                                    <FontAwesomeIcon icon={faEye} />
                                </span>
                                <span>View Profile</span>
                            </Link>
                            <Link to="/dashboard" className="button is-light">
                                <span className="icon">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </span>
                                <span>Back to Dashboard</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {notification.message && (
                    <Notification 
                        type={notification.type} 
                        message={notification.message} 
                        onClose={closeNotification} 
                    />
                )}

                {/* Profile Form */}
                <div className="box">
                    <form onSubmit={handleSubmit}>
                        <div className="columns is-multiline">
                            {/* Personal Information Column */}
                            <div className="column is-6">
                                <div className="field">
                                    <label className="label">First Name</label>
                                    <div className="control">
                                        <input
                                            className={`input ${errors.first_name ? "is-danger" : ""}`}
                                            type="text"
                                            name="first_name"
                                            value={profile.first_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    {errors.first_name && (
                                        <p className="help is-danger">{errors.first_name}</p>
                                    )}
                                </div>

                                <div className="field">
                                    <label className="label">Last Name</label>
                                    <div className="control">
                                        <input
                                            className={`input ${errors.last_name ? "is-danger" : ""}`}
                                            type="text"
                                            name="last_name"
                                            value={profile.last_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    {errors.last_name && (
                                        <p className="help is-danger">{errors.last_name}</p>
                                    )}
                                </div>

                                <div className="field">
                                    <label className="label">Email</label>
                                    <div className="control">
                                        <input
                                            className={`input ${errors.email ? "is-danger" : ""}`}
                                            type="email"
                                            name="email"
                                            value={profile.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    {errors.email && (
                                        <p className="help is-danger">{errors.email}</p>
                                    )}
                                </div>
                            </div>

                            {/* Contact Information Column */}
                            <div className="column is-6">
                                <div className="field">
                                    <label className="label">Phone Number</label>
                                    <div className="control">
                                        <input
                                            className={`input ${errors.phone_number ? "is-danger" : ""}`}
                                            type="tel"
                                            name="phone_number"
                                            value={profile.phone_number}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    {errors.phone_number && (
                                        <p className="help is-danger">{errors.phone_number}</p>
                                    )}
                                </div>

                                <div className="field">
                                    <label className="label">Status</label>
                                    <div className="control">
                                        <div className="select is-fullwidth">
                                            <select
                                                name="status"
                                                value={profile.status}
                                                onChange={handleChange}
                                                disabled // Typically status shouldn't be editable by user
                                            >
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="column is-12">
                                <div className="field is-grouped is-grouped-right">
                                    <div className="control">
                                        <button
                                            type="submit"
                                            className={`button is-primary ${isSubmitting ? "is-loading" : ""}`}
                                            disabled={isSubmitting}
                                        >
                                            <span className="icon">
                                                <FontAwesomeIcon icon={faSave} />
                                            </span>
                                            <span>Save Changes</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ProfileUpdate;